<button
  #filterOrigin="cdkOverlayOrigin"
  cdkOverlayOrigin
  type="button"
  mat-stroked-button
  class="av-filter-panel__trigger"
  [class.is-active]="activeFilterCount() > 0"
  [attr.aria-expanded]="panelOpen()"
  [attr.aria-label]="'app.reports.allDemands.filters.trigger' | translate"
  (click)="togglePanel()"
>
  <mat-icon>filter_list</mat-icon>
  <span class="av-filter-panel__trigger-label">{{
    'app.reports.allDemands.filters.trigger' | translate
  }}</span>
  @if (activeFilterCount() > 0) {
    <span class="av-filter-panel__badge">{{ activeFilterCount() }}</span>
  }
</button>

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="filterOrigin"
  [cdkConnectedOverlayOpen]="panelOpen()"
  [cdkConnectedOverlayPositions]="overlayPositions"
  [cdkConnectedOverlayHasBackdrop]="true"
  cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
  (backdropClick)="closePanel()"
  (detach)="closePanel()"
  (overlayKeydown)="onOverlayKeydown($event)"
>
  <div
    class="av-filter-panel__panel mat-elevation-z4"
    role="dialog"
    [attr.aria-label]="'app.reports.allDemands.filters.title' | translate"
    (click)="$event.stopPropagation()"
  >
    <header class="av-filter-panel__header d-flex align-items-center justify-content-between">
      <h2 class="av-filter-panel__title m-0">
        {{ 'app.reports.allDemands.filters.title' | translate }}
      </h2>
      <button
        type="button"
        mat-icon-button
        [attr.aria-label]="'shared.general.buttons.close' | translate"
        (click)="closePanel()"
      >
        <mat-icon>close</mat-icon>
      </button>
    </header>

    <div class="av-filter-panel__section">
      <span class="av-filter-panel__section-label">{{
        'app.reports.allDemands.filters.status' | translate
      }}</span>
      <div class="av-filter-panel__row" role="group">
        <button
          type="button"
          class="av-filter-panel__pill"
          [class.is-active]="criteria().statuses.length === 0"
          [attr.aria-pressed]="criteria().statuses.length === 0"
          (click)="onClearStatuses()"
        >
          {{ 'app.reports.allDemands.filters.statusAll' | translate }}
        </button>
        @for (status of statusValues; track status) {
          <button
            type="button"
            class="av-filter-panel__pill"
            [class.is-active]="isStatusActive(status)"
            [attr.aria-pressed]="isStatusActive(status)"
            (click)="onToggleStatus(status)"
          >
            {{ 'app.catalog.demand.status.' + status | translate }}
          </button>
        }
      </div>
    </div>

    <div class="av-filter-panel__section">
      <span class="av-filter-panel__section-label">{{
        'app.reports.allDemands.filters.platform' | translate
      }}</span>
      <div class="av-filter-panel__row" role="group">
        <button
          type="button"
          class="av-filter-panel__pill"
          [class.is-active]="criteria().platform === null"
          [attr.aria-pressed]="criteria().platform === null"
          (click)="onPlatformChange(null)"
        >
          {{ 'app.reports.allDemands.filters.platformAll' | translate }}
        </button>
        @for (option of platformOptions; track option.value) {
          <button
            type="button"
            class="av-filter-panel__pill"
            [class.is-active]="criteria().platform === option.value"
            [attr.aria-pressed]="criteria().platform === option.value"
            (click)="onPlatformChange(option.value)"
          >
            {{ option.labelKey | translate }}
          </button>
        }
      </div>
      @if (inertOnGroups()) {
        <span class="av-filter-panel__note">{{
          'app.reports.allDemands.filters.notApplicableToGroups' | translate
        }}</span>
      }
    </div>

    <div class="av-filter-panel__grid">
      <app-all-demands-date-range
        [label]="'app.reports.allDemands.filters.requestedDateRange'"
        [from]="criteria().requestedFrom"
        [to]="criteria().requestedTo"
        (rangeChange)="onRequestedRangeChange($event)"
      />
      <app-all-demands-date-range
        [label]="'app.reports.allDemands.filters.updatedDateRange'"
        [from]="criteria().updatedFrom"
        [to]="criteria().updatedTo"
        (rangeChange)="onUpdatedRangeChange($event)"
      />

      <app-all-demands-picker
        [label]="'app.reports.allDemands.filters.requester'"
        [options]="requesterOptions()"
        [selectedId]="criteria().requesterId"
        [state]="optionsState()"
        (selectedIdChange)="onRequesterChange($event)"
      />
      <app-all-demands-picker
        [label]="'app.reports.allDemands.filters.subject'"
        [options]="subjectOptions()"
        [selectedId]="criteria().subjectId"
        [state]="optionsState()"
        (selectedIdChange)="onSubjectChange($event)"
      />

      <app-all-demands-picker
        [label]="'app.reports.allDemands.filters.approver'"
        [hint]="'app.reports.allDemands.filters.approverHint'"
        [options]="approverOptions()"
        [selectedId]="criteria().approverId"
        [state]="optionsState()"
        (selectedIdChange)="onApproverChange($event)"
      />
      <app-all-demands-picker
        [label]="'app.reports.allDemands.filters.moderator'"
        [options]="moderatorOptions()"
        [selectedId]="criteria().moderatorId"
        [state]="optionsState()"
        (selectedIdChange)="onModeratorChange($event)"
      />

      <div class="av-filter-panel__device d-flex flex-column">
        <span class="av-filter-panel__section-label">{{
          'app.reports.allDemands.filters.device' | translate
        }}</span>
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <input
            matInput
            type="text"
            autocomplete="off"
            [formControl]="deviceControl"
            [maxlength]="deviceMaxLength"
            [attr.aria-label]="'app.reports.allDemands.filters.device' | translate"
            [placeholder]="'app.reports.allDemands.filters.devicePlaceholder' | translate"
          />
          @if (inertOnGroups()) {
            <mat-hint>{{
              'app.reports.allDemands.filters.notApplicableToGroups' | translate
            }}</mat-hint>
          }
        </mat-form-field>
      </div>
    </div>

    <div class="av-filter-panel__footer d-flex justify-content-end">
      <button type="button" mat-button [disabled]="!hasFilters()" (click)="onClearAll()">
        {{ 'app.reports.allDemands.filters.clearAll' | translate }}
      </button>
    </div>
  </div>
</ng-template>
